import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Popover, Typography, Box, Grid, CircularProgress, styled } from '@mui/material';
import useSubmitForm from '../../../hooks/useSubmitForm'; // Adjust path
import RTL from '../../../utils/RTL';
import StyledButton from '../../CustomButton';
import { renderField } from './formUtils';
import ConfirmationDialog from './ConfirmationDialog';
import FloatingSnackbar from '../../common/FloatingSnackbar';

// Styled components (unchanged)
const PageBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: theme.palette.background.default,
}));

const InnerBox = styled(Box)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    minHeight: '60vh',
    minWidth: '50%',
}));

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    '& > :not(style)': {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
    [theme.breakpoints.up('md')]: {
        width: '70%',
    },
}));

export default function GenericCreate({
    formTitle,
    initialState,
    submitUrl,
    instanceConfig,
    userRole,
    onFieldChange,   // optional
    onFormChange,    // optional
    submitMethod = 'POST', // NEW: allows PATCH for edit mode
    dense = false, // compact layout: full-width form, no min-height, tighter field spacing
    // Confirmation dialog props
    shouldConfirm = false,
    confirmationTitle = 'تأیید ویرایش',
    confirmationMessage = 'آیا از ویرایش این دوره اطمینان دارید؟',
}) {
    
    const {
        formData, handleChange, handleSubmit, openSnackbar, errors, isLoading,
        anchorEl, setAnchorEl, snackbarMessage, handleCloseSnackbar, setOpenSnackbar, resetForm
    } = useSubmitForm(initialState, submitUrl, submitMethod, null, instanceConfig);
    
    // Reset form when initialState changes (for edit mode)
    useEffect(() => {
        if (resetForm) {
            resetForm();
        }
    }, [initialState, resetForm]);
    
    // Confirmation dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [pendingAfterSubmit, setPendingAfterSubmit] = useState(null);
    const [pendingEvent, setPendingEvent] = useState(null);
    // Proxy that notifies parent while preserving original handleChange contract
    const notifyChange = (fieldKey, valueOrEvent) => {
        // Support both signatures: (key, value) and ({ target: { name, value } })
        if (typeof valueOrEvent === 'object' && valueOrEvent?.target) {
            // file input path in your renderField still uses event-like object in 'file' case
            handleChange(valueOrEvent);
            const next = { ...formData, [valueOrEvent.target.name]: valueOrEvent.target.value };

            // If user switches to individual, clear capacity in form state
            if (fieldKey === "course_type" && valueOrEvent === "individual") {
                handleChange("capacity", "");
                next.capacity = "";
            }
            
            // If user switches payment type, clear the opposite field
            if (fieldKey === "course_payment_type") {
                const newPaymentType = valueOrEvent.target.value;
                if (newPaymentType === "fixed") {
                    // Set teacher_percentage to 0 when switching to fixed
                    handleChange("teacher_percentage", "0");
                    next.teacher_percentage = "0";
                } else if (newPaymentType === "percentage") {
                    // Set fixed_course_salary to 0 when switching to percentage
                    handleChange("fixed_course_salary", "0");
                    next.fixed_course_salary = "0";
                }
            }
            
            onFieldChange?.(valueOrEvent.target.name, valueOrEvent.target.value, next);
            onFormChange?.(next);
            return;
        }
        handleChange(fieldKey, valueOrEvent);
        const next = { ...formData, [fieldKey]: valueOrEvent };
        
        // Handle payment type changes for non-event values (radio buttons)
        if (fieldKey === "course_payment_type") {
            if (valueOrEvent === "fixed") {
                // Set teacher_percentage to 0 when switching to fixed
                handleChange("teacher_percentage", "0");
                next.teacher_percentage = "0";
            } else if (valueOrEvent === "percentage") {
                // Set fixed_course_salary to 0 when switching to percentage
                handleChange("fixed_course_salary", "0");
                next.fixed_course_salary = "0";
            }
        }
        
        onFieldChange?.(fieldKey, valueOrEvent, next);
        onFormChange?.(next);
    };
    // Filter fields based on user role (e.g., exclude profile_type/role for secretaries)
    const filteredFields = userRole === 'secretary'
        ? Object.fromEntries(Object.entries(instanceConfig.fields).filter(([key]) => !['profile_type', 'role'].includes(key)))
        : instanceConfig.fields;

    // Handle form submission with optional confirmation
    const onSubmit = useCallback(async (event) => {
        try {
            await handleSubmit(event, ({ resetForm, setSnackbar }) => {
                // CRITICAL: Always call instanceConfig.postSubmit first
                // This is where the page's onAfterSubmit is wired through Config.jsx
                if (instanceConfig?.postSubmit) {
                    instanceConfig.postSubmit({ resetForm, setSnackbar });
                }
            });
        } catch (err) {
            // Error in form submission
        }
    }, [handleSubmit, instanceConfig]);

    const handleFormSubmit = useCallback((event, onAfterSuccess) => {
        // Always prevent default to avoid page reload
        if (event) event.preventDefault();
        
        if (shouldConfirm) {
            // Store the event for later submission
            setPendingEvent(event);
            setShowDialog(true);
        } else {
            // Direct submission without confirmation
            onSubmit(event);
        }
    }, [shouldConfirm, onSubmit]);

    // Handle confirmation dialog actions
    const handleConfirmSubmit = () => {
        setShowDialog(false);
        // Call onSubmit with the stored event
        if (pendingEvent) {
            onSubmit(pendingEvent);
        }
        setPendingEvent(null);
    };

    const handleCancelSubmit = () => {
        setShowDialog(false);
        setPendingEvent(null);
    };

    const gridRef = useRef(null);

    return (
        <RTL>
            <PageBox>
                <InnerBox sx={dense ? { minHeight: 'auto', minWidth: '100%', p: 0 } : undefined}>
                    {formTitle && (
                        <Typography variant="h4" sx={{ marginBottom: '1.5rem' }}>{formTitle}</Typography>
                    )}
                    <StyledForm noValidate autoComplete="off" sx={dense ? { width: { xs: '100%', md: '100%' } } : undefined} onSubmit={(e) => {
                        handleFormSubmit(e, instanceConfig?.postSubmit);
                    }}>
                        {/* Use explicit item paddings to avoid RTL-only right padding from Grid spacing */}
                        <Grid
                            container
                            spacing={0}
                            justifyContent="center"
                            alignItems="center"
                            ref={gridRef}
                            sx={{
                                '& .MuiTextField-root': { m: 0 },
                                '& .MuiFormControl-root': { m: 0 },
                            }}
                        >
                            {Object.keys(filteredFields).map((key) => {
                                const fieldConfig = filteredFields[key];
                                return (
                                    <Grid key={key} item xs={12} sm={6} data-field-key={key} sx={{ px: 2, pb: dense ? 2.5 : 4 }}>
                                        {renderField(fieldConfig, key, formData, notifyChange, errors)}
                                    </Grid>
                                );
                            })}
                            <Grid item xs={12} sm={6} textAlign="center" sx={{ px: 2, pb: dense ? 2.5 : 4 }}>
                                <StyledButton type="submit" disabled={isLoading}>
                                    {isLoading ? <CircularProgress size={24} /> : 'ذخیره'}
                                </StyledButton>
                            </Grid>
                        </Grid>
                        {errors.form && <Typography color="error">{errors.form}</Typography>}
                    </StyledForm>
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <Box p={2}>
                            <Typography>{snackbarMessage}</Typography>
                        </Box>
                    </Popover>
                    <FloatingSnackbar
                        open={openSnackbar}
                        message={snackbarMessage}
                        severity="info"
                        onClose={handleCloseSnackbar}
                        anchor={{ vertical: 'bottom', horizontal: 'left' }}
                        autoHideDuration={6000}
                    />
                </InnerBox>
            </PageBox>
            
            {/* Confirmation Dialog */}
            {shouldConfirm && (
                <ConfirmationDialog
                    open={showDialog}
                    onClose={handleCancelSubmit}
                    onConfirm={handleConfirmSubmit}
                    title={confirmationTitle}
                    message={confirmationMessage}
                    confirmText="تأیید ویرایش"
                    cancelText="لغو"
                    confirmColor="primary"
                    isLoading={isLoading}
                />
            )}
        </RTL>
    );
}